// src/hooks/useUserDatabase.js - Database Management Hook
import { useState, useEffect, useCallback, useRef } from 'react';
import logger from '../services/logger';

export const useUserDatabase = () => {
  // Core state
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Session tracking
  const [currentSession, setCurrentSession] = useState(null);
  const [isLogging, setIsLogging] = useState(false);
  
  // Refs for batching and optimization
  const sessionBufferRef = useRef([]);
  const logTimeoutRef = useRef(null);
  const lastPlatformRef = useRef(null);
  
  // Initialize user from storage and validate token
  useEffect(() => {
    initializeUser();
  }, []);

  const initializeUser = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('user');
      
      if (!token || !userData) {
        setIsAuthenticated(false);
        setUser(null);
        setLoading(false);
        return;
      }
      
      // Validate token with backend
      const response = await fetch('/api/user/me', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token
        }
      });
      
      if (response.ok) {
        const validatedUser = await response.json();
        setUser(validatedUser);
        setIsAuthenticated(true);
        
        // Update local storage with latest user data
        localStorage.setItem('user', JSON.stringify(validatedUser));
        
        logger.success('User authenticated:', validatedUser.email);
      } else {
        // Token invalid, clear storage
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
        setUser(null);
        logger.error('Token validation failed, user logged out');
      }
    } catch (err) {
      logger.error('Error initializing user:', err);
      setError('Failed to validate user session');
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Authentication methods
  const login = useCallback(async (credentials) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(credentials)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Store token and user data
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        setUser(data.user);
        setIsAuthenticated(true);
        
        logger.success('Login successful:', data.user.email);
        return { success: true, user: data.user };
      } else {
        setError(data.error || 'Login failed');
        return { success: false, error: data.error };
      }
    } catch (err) {
      const errorMsg = 'Network error during login';
      setError(errorMsg);
      logger.error('Login error:', err);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (userData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Store token and user data
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        setUser(data.user);
        setIsAuthenticated(true);
        
        logger.success('Registration successful:', data.user.email);
        return { success: true, user: data.user };
      } else {
        setError(data.error || 'Registration failed');
        return { success: false, error: data.error };
      }
    } catch (err) {
      const errorMsg = 'Network error during registration';
      setError(errorMsg);
      logger.error('Registration error:', err);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      // Flush any pending logs before logout
      await flushSessionBuffer();
      
      // Clear local storage
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('guestSession');
      
      // Reset state
      setUser(null);
      setIsAuthenticated(false);
      setCurrentSession(null);
      setError(null);
      
      // Clear session tracking
      sessionBufferRef.current = [];
      lastPlatformRef.current = null;
      
      if (logTimeoutRef.current) {
        clearTimeout(logTimeoutRef.current);
        logTimeoutRef.current = null;
      }
      
      logger.success('User logged out successfully');
    } catch (err) {
      logger.error('Error during logout:', err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Session and platform tracking
  const startPlatformSession = useCallback(async (platform, platformUserId) => {
    if (!isAuthenticated || !user) {
      logger.info('Not authenticated, skipping platform session start');
      return;
    }

    try {
      const sessionData = {
        user_id: user.id,
        platform,
        platform_user_id: platformUserId,
        session_start: new Date().toISOString(),
        dashboard_entered: null
      };

      // Store locally for immediate use
      setCurrentSession(sessionData);
      lastPlatformRef.current = platform;
      
      logger.platform(`Started ${platform} session for user ${user.email}`);

    } catch (err) {
      logger.error('Error starting platform session:', err);
    }
  }, [isAuthenticated, user]);

  const logDashboardEntry = useCallback(async () => {
    if (!currentSession) return;
    
    try {
      const updatedSession = {
        ...currentSession,
        dashboard_entered: new Date().toISOString()
      };
      
      setCurrentSession(updatedSession);
      logger.debug(`Dashboard entered for ${currentSession.platform} session`);
    } catch (err) {
      logger.error('Error logging dashboard entry:', err);
    }
  }, [currentSession]);

  // Listening session tracking with batching
  const logListeningSession = useCallback(async (trackData) => {
    if (!isAuthenticated || !user || !currentSession) {
      return; // Skip logging for unauthenticated users
    }

    try {
      const sessionData = {
        user_id: user.id,
        session_id: currentSession.session_id || generateSessionId(),
        timestamp: new Date().toISOString(),
        platform: currentSession.platform,
        platform_user_id: currentSession.platform_user_id,
        track_uri: trackData.uri,
        track_name: trackData.name,
        artists: Array.isArray(trackData.artists) 
          ? trackData.artists 
          : [trackData.artists].filter(Boolean),
        mood: trackData.mood || null,
        energy_level: trackData.energy_level || 'medium',
        played_full: trackData.played_full || false,
        skipped: trackData.skipped || false,
        added_by_dj: trackData.added_by_dj || false,
        dj_prompt_used: trackData.dj_prompt_used || null,
        dj_voice_variant: trackData.dj_voice_variant || null
      };

      // Add to buffer for batching
      sessionBufferRef.current.push(sessionData);
      
      logger.debug(`Logged listening session: "${trackData.name}" on ${currentSession.platform}`);
      
      // Schedule batch flush
      if (logTimeoutRef.current) {
        clearTimeout(logTimeoutRef.current);
      }
      
      logTimeoutRef.current = setTimeout(() => {
        flushSessionBuffer();
      }, 5000); // Batch every 5 seconds

    } catch (err) {
      logger.error('Error logging listening session:', err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, currentSession]);

  // DJ interaction logging
  const logDJInteraction = useCallback(async (djData) => {
    if (!isAuthenticated || !user || !currentSession) {
      return;
    }

    try {
      logger.platform(`Logged DJ interaction: "${djData.dj_prompt?.substring(0, 50)}..."`);

    } catch (err) {
      logger.error('Error logging DJ interaction:', err);
    }
  }, [isAuthenticated, user, currentSession]);

  // Recommendation event logging
  const logRecommendationEvent = useCallback(async (recommendationData) => {
    if (!isAuthenticated || !user) {
      return;
    }

    try {
      const eventData = {
        user_id: user.id,
        timestamp: new Date().toISOString(),
        seed_context: {
          recent_tracks: recommendationData.recent_tracks || [],
          genres_detected: recommendationData.genres_detected || [],
          mood_input: recommendationData.mood_input || null,
          exploration_mode: recommendationData.exploration_mode || 'balanced'
        },
        engine_used: recommendationData.engine_used || 'ai-v1',
        result_uris: recommendationData.result_uris || [],
        reason: recommendationData.reason || 'user_triggered',
        notes: recommendationData.notes || null
      };

      logger.debug(`Logged recommendation event: ${eventData.result_uris.length} tracks added`);

    } catch (err) {
      logger.error('Error logging recommendation event:', err);
    }
  }, [isAuthenticated, user]);

  // User preferences management
  const updateUserPreferences = useCallback(async (preferences) => {
    if (!isAuthenticated || !user) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      setLoading(true);
      
      // Local-only for beta: persists to localStorage, not backend
      const updatedUser = {
        ...user,
        preferences: { ...user.preferences, ...preferences }
      };
      
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      logger.success('User preferences updated:', preferences);
      return { success: true };
      
    } catch (err) {
      logger.error('Error updating preferences:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  // Helper functions
  const makeAuthenticatedRequest = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem('authToken');
    
    if (!token) {
      throw new Error('No authentication token');
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-auth-token': token,
        ...options.headers
      }
    });

    if (response.status === 401) {
      // Token expired, log out user
      await logout();
      throw new Error('Authentication expired');
    }

    return response;
  }, [logout]);

  const flushSessionBuffer = useCallback(async () => {
    if (sessionBufferRef.current.length === 0) return;

    try {
      setIsLogging(true);
      
      const sessions = [...sessionBufferRef.current];
      sessionBufferRef.current = []; // Clear buffer
      
      logger.debug(`Flushing ${sessions.length} listening sessions (local-only for beta)`);
      logger.success('Session buffer flushed successfully');
      
    } catch (err) {
      logger.error('Error flushing session buffer:', err);
      // Put sessions back in buffer if failed
     // sessionBufferRef.current = [...sessionBufferRef.current, ...sessions];
    } finally {
      setIsLogging(false);
    }
  }, []);

  const generateSessionId = () => {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (logTimeoutRef.current) {
        clearTimeout(logTimeoutRef.current);
      }
      // Flush any remaining sessions
      if (sessionBufferRef.current.length > 0) {
        flushSessionBuffer();
      }
    };
  }, [flushSessionBuffer]);

  // Auto-flush sessions periodically
  useEffect(() => {
    const flushInterval = setInterval(() => {
      if (sessionBufferRef.current.length > 0) {
        flushSessionBuffer();
      }
    }, 30000); // Flush every 30 seconds

    return () => clearInterval(flushInterval);
  }, [flushSessionBuffer]);

  // Public API
  return {
    // Authentication state
    user,
    isAuthenticated,
    loading,
    error,
    
    // Authentication methods
    login,
    register,
    logout,
    
    // Session tracking
    currentSession,
    startPlatformSession,
    logDashboardEntry,
    
    // Data logging
    logListeningSession,
    logDJInteraction,
    logRecommendationEvent,
    
    // User management
    updateUserPreferences,
    
    // Utility
    makeAuthenticatedRequest,
    isLogging,
    
    // Manual controls
    flushSessionBuffer: flushSessionBuffer,
    refreshUser: initializeUser
  };
};